import { copilotContext as e, copilotInstructions as t, copilotTools as n, uniqueId as r } from "./global-state.js";
import { hasHiddenInstruction as i } from "./hidden-instruction.js";
import { CopilotRunState as a } from "./run-stream.js";
import { atom as o, computed as s, map as c } from "nanostores";
//#region src/session.ts
function l() {
	return Math.floor(Date.now() / 1e3);
}
var u = class extends Error {
	constructor(e = "The agent is still working on the previous turn.", t) {
		super(e), this.name = "CopilotActiveExchangeError", this.cause = t?.cause;
	}
}, d = {
	idle: ["initializing", "ending"],
	initializing: ["ready", "error"],
	ready: [
		"sending",
		"ending",
		"recovering"
	],
	sending: ["receiving", "recovering"],
	receiving: ["ready", "recovering"],
	recovering: ["ready", "ending"],
	ending: ["ended", "error"],
	error: ["initializing", "ending"],
	ended: []
}, f = "The previous tool call did not complete. The action may not have run. Tell the user the action did not complete and offer to try again — do not claim success or failure.", p = 2e3, m = 500, h = 3, g = 3e4, ee = 6e4, te = 1e4, _ = 1e4, v = 18e5, y = "Something went wrong and the agent couldn't reply. Please try again.", b = 6e4;
function x(e) {
	return e < ee ? p : te;
}
var S = "cio-copilot-approval-mode";
function ne() {
	try {
		let e = window.localStorage.getItem(S);
		return e === "ask" || e === "auto" ? e : null;
	} catch {
		return null;
	}
}
function C(e) {
	try {
		window.localStorage.setItem(S, e);
	} catch {}
}
function w(e, t) {
	if (e === t) return e;
	if (!d[e]) throw Error(`Unknown status: ${e}`);
	if (d[e].includes(t)) return t;
	throw Error(`Invalid status transition: ${e} -> ${t}`);
}
function T(e) {
	return e.type === "meta" || e.type === "error" && e.client_generated !== !0;
}
function E(e, t) {
	return J(e, t).filter(T).length;
}
function D(e) {
	for (let t = e.length - 1; t >= 0; t--) {
		let n = e[t];
		if (n.type === "meta") return n.meta?.id;
	}
}
function O(e) {
	let t = /* @__PURE__ */ new Set();
	for (let n of e) if (n.role === "user" && n.type === "tool_response") for (let e of n.tool_responses) t.add(e.tool_call_id);
	return e.some((e) => e.role === "model" && e.type === "tool_call" && e.tool_calls.some((e) => e.is_client_tool === !0 && !t.has(e.id)));
}
var k = class {
	get id() {
		return this.__id.get();
	}
	set id(e) {
		this.__id.set(e == null ? void 0 : String(e));
	}
	constructor(r) {
		this.__id = o(void 0), this.config = {
			includeGlobalContext: !0,
			includeGlobalTools: !0,
			includeGlobalInstructions: !0
		}, this.__status = o("idle"), this.__messages = o([]), this.__meta = o({}), this.__uploadFiles = o([]), this.__queuedSends = o([]), this.__queuedMessages = s(this.__queuedSends, (e) => e.flatMap((e) => e.messages)), this.__restoredMessages = o([]), this.__lastTurnStopped = o(!1), this.__orphanedToolCallIds = o(/* @__PURE__ */ new Set()), this.__abortController = null, this.__inFlightClientMessageId = void 0, this.__recoveryStopTimeout = null, this.__wakeRecoveryPoll = null, this.__recoveryGeneration = 0, this.__enableWebSearch = o(!0), this.__approvalMode = o(ne() ?? "ask"), this.__approvalModeAvailable = o(!1), this.__model = o(""), this.__modelLocked = s(this.__id, (e) => e != null), this.__serverSideTools = c({}), this.__sessionInstructions = c({}), this.__sessionTools = c({}), this.__sessionContext = c({}), this.__tools = s([
			this.__serverSideTools,
			n,
			this.__sessionTools
		], (e, t, n) => {
			let r = {}, i = {
				...this.config.includeGlobalTools === !0 ? t : {},
				...n
			};
			for (let t of Object.values(e)) if (t != null) {
				let e = i[t.name];
				r[t.name] = {
					...e?.type === "server" ? e : void 0,
					...t
				};
			}
			for (let e of Object.values(i)) e != null && e.type === "client" && r[e.name] == null && (r[e.name] = e);
			return r;
		}), this.__lastStatefulMessages = [], this.__statefulMessages = s([
			this.__messages,
			this.__tools,
			this.__orphanedToolCallIds
		], (e, t, n) => {
			let r = F(e, t, n, (e, t, n) => this.respondToToolCall(e, t, n));
			return this.__lastStatefulMessages = I(this.__lastStatefulMessages, r), this.__lastStatefulMessages;
		}), this.__instructions = s([t, this.__sessionInstructions], (e, t) => ({
			...this.config.includeGlobalInstructions === !0 ? e : {},
			...t
		})), this.__context = s([e, this.__sessionContext], (e, t) => ({
			...this.config.includeGlobalContext === !0 ? e : {},
			...t
		})), this.__respondedToolCallIds = /* @__PURE__ */ new Set(), this.__redispatchingToolCallIds = /* @__PURE__ */ new Set(), this.__toolsUnsub = null, this.__suggestionsRefreshTrigger = o([]), r && (this.config = {
			...this.config,
			...r
		});
	}
	get status() {
		return this.__status.get();
	}
	get isEnding() {
		return this.status === "ending";
	}
	get isEnded() {
		return this.status === "ended";
	}
	setStatus(e) {
		let t = this.status, n = w(t, e);
		n === "recovering" && t !== "recovering" && (this.__recoveryGeneration += 1), n !== "recovering" && (this.__recoveryStopTimeout != null && (clearTimeout(this.__recoveryStopTimeout), this.__recoveryStopTimeout = null), this.__wakeRecoveryPoll?.()), this.__status.set(n);
	}
	addInstruction(e) {
		let t = r();
		return this.__sessionInstructions.setKey(t, e), () => {
			this.__sessionInstructions.setKey(t, void 0);
		};
	}
	addTool(e) {
		return this.__sessionTools.setKey(e.name, e), () => {
			this.__sessionTools.setKey(e.name, void 0);
		};
	}
	addContext(e) {
		let t = r();
		return this.__sessionContext.setKey(t, e), () => {
			this.__sessionContext.setKey(t, void 0);
		};
	}
	setSessionMeta(e) {
		this.__meta.set(e);
	}
	getSessionMeta() {
		return this.__meta.get();
	}
	setWebSearchEnabled(e) {
		this.__enableWebSearch.set(e);
	}
	getWebSearchEnabled() {
		return this.__enableWebSearch.get();
	}
	applyServerModel(e) {
		this.__model.set(typeof e?.model == "string" ? e.model : "");
	}
	setModel(e) {
		this.__modelLocked.get() || this.__model.set(e);
	}
	getModel() {
		return this.__model.get();
	}
	isModelLocked() {
		return this.__modelLocked.get();
	}
	setApprovalMode(e) {
		this.__approvalMode.set(e), C(e);
	}
	setApprovalModeAvailable(e) {
		this.__approvalModeAvailable.set(e);
	}
	getApprovalMode() {
		return this.__approvalModeAvailable.get() ? this.__approvalMode.get() : "ask";
	}
	setMessageMeta(e, t) {
		let n = this.__messages.get().map((n) => n.role === "model" && n.type === "meta" && "meta" in n && n.meta?.id === e ? {
			...n,
			meta: t
		} : n);
		this.__messages.set(n);
	}
	respondToToolCall(e, t, n) {
		this.__respondedToolCallIds.has(e) || (this.__respondedToolCallIds.add(e), this.sendMessage([{
			role: "user",
			type: "tool_response",
			tool_responses: [{
				tool_call_id: e,
				name: t,
				response: {
					result: n,
					error: null
				},
				finished: !0
			}],
			timestamp: l()
		}]).catch(() => {
			this.__respondedToolCallIds.delete(e);
		}));
	}
	clearQueuedMessages() {
		let e = this.__queuedMessages.get();
		return this.__queuedSends.set([]), e;
	}
	clearRestoredMessages() {
		this.__restoredMessages.set([]);
	}
	surfaceQueuedMessagesForRestore() {
		let e = this.clearQueuedMessages();
		e.length > 0 && this.__restoredMessages.set([...this.__restoredMessages.get(), ...e]);
	}
	settleReady() {
		this.markOrphanedToolCalls(), this.status === "sending" && this.setStatus("receiving"), this.setStatus("ready");
	}
	settleReadyAfterFailure() {
		this.settleReady(), this.surfaceQueuedMessagesForRestore();
	}
	scheduleRecoveryStopFallback() {
		this.__recoveryStopTimeout ??= setTimeout(() => {
			this.__recoveryStopTimeout = null, this.status === "recovering" && this.settleReadyAfterFailure();
		}, _), this.__wakeRecoveryPoll?.();
	}
	waitForRecoveryPoll(e) {
		return new Promise((t) => {
			let n = setTimeout(() => {
				this.__wakeRecoveryPoll = null, t(!0);
			}, e);
			this.__wakeRecoveryPoll = () => {
				clearTimeout(n), this.__wakeRecoveryPoll = null, t(!1);
			};
		});
	}
	queueSend(e, t) {
		let n = this.__queuedSends.get(), r = n[n.length - 1];
		if (t.length === 0 && r?.instructions.length === 0 && !r.messages.some(B) && !e.some(B)) {
			this.__queuedSends.set([...n.slice(0, -1), {
				...r,
				messages: [...r.messages, ...e]
			}]);
			return;
		}
		this.__queuedSends.set([...n, {
			messages: e,
			instructions: t
		}]);
	}
	queueLatestUnsentPrompt(e, t) {
		let n = K(e);
		if (n.length === 0) return;
		let r = this.__queuedSends.get().filter((e) => !G(e.messages));
		this.__queuedSends.set([...r, {
			messages: n,
			instructions: t
		}]);
	}
	hasQueuedUnsentPrompt() {
		return this.__queuedSends.get().some((e) => G(e.messages));
	}
	dropQueuedPromptsAlreadyInMessages(e) {
		let t = this.__queuedSends.get(), n = t.filter((t) => !G(t.messages) || !me(e, t.messages));
		n.length !== t.length && this.__queuedSends.set(n);
	}
	async stop() {
		if (this.status !== "sending" && this.status !== "receiving" && this.status !== "recovering") return { messages: [] };
		if (this.__lastTurnStopped.set(!0), this.status === "recovering") {
			let e = this.clearQueuedMessages(), t = this.__recoveryGeneration, n = window.__hydra_copilot__?.stopExchange;
			if (n == null || this.id == null) return this.settleReady(), { messages: e };
			this.scheduleRecoveryStopFallback();
			try {
				await n({ id: this.id });
			} catch (n) {
				return this.status === "recovering" && this.__recoveryGeneration === t ? (this.settleReadyAfterFailure(), {
					messages: e,
					error: n instanceof Error ? n : Error(String(n))
				}) : { messages: e };
			}
			return { messages: e };
		}
		let e = this.__inFlightClientMessageId;
		this.__abortController?.abort();
		let t = this.clearQueuedMessages(), n = window.__hydra_copilot__?.stopExchange;
		if (n != null && this.id != null) try {
			await n({
				id: this.id,
				clientMessageId: e
			});
		} catch (e) {
			return {
				messages: t,
				error: e instanceof Error ? e : Error(String(e))
			};
		}
		return { messages: t };
	}
	refreshSuggestions(e) {
		this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
	}
	async initialize(e) {
		if (this.status !== "idle" && this.status !== "error") return this.id;
		this.setStatus("initializing");
		try {
			let t = window.__hydra_copilot__?.startSession;
			if (t == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
			if (e == null) {
				let e = this.__model.get(), n = await t(e);
				if (n.id == null) throw Error("Failed to start session, no session ID returned");
				this.id = n.id, this.__model.set(e), this.__serverSideTools.set($(n.tools ?? []));
			} else {
				this.__approvalMode.set("ask"), this.id = e;
				let t = await this.loadSession();
				if (this.__messages.set(t.messages ?? []), this.__meta.set(t.meta ?? {}), this.applyServerModel(t.meta), this.__serverSideTools.set($(t.tools ?? [])), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set()), t.exchangeRunning === !0) return this.setStatus("ready"), this.followExchangeRunningElsewhere(t.messages ?? []), this.id;
			}
			return this.setStatus("ready"), this.executePendingClientToolCalls(), this.id;
		} catch (e) {
			throw this.setStatus("error"), e;
		}
	}
	async refresh(e = {}) {
		if (!(this.id == null || this.status !== "ready")) try {
			let t = await this.loadSession();
			if (this.status !== "ready") return;
			let n = e.onlyIfChangedElsewhere === !0;
			if (t.exchangeRunning === !0) {
				n || (this.__messages.set(t.messages ?? []), this.__meta.set(t.meta ?? {}), this.applyServerModel(t.meta), this.__serverSideTools.set($(t.tools ?? []))), this.followExchangeRunningElsewhere(t.messages ?? []);
				return;
			}
			if (n) {
				let e = t.messages ?? [];
				if (D(e) === D(this.__messages.get()) || O(e)) return;
			}
			this.__messages.set(t.messages ?? []), this.__meta.set(t.meta ?? {}), this.applyServerModel(t.meta), this.__serverSideTools.set($(t.tools ?? [])), n || this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set()), this.executePendingClientToolCalls();
		} catch {}
	}
	async end() {
		this.setStatus("ending");
		try {
			let e = window.__hydra_copilot__?.endSession;
			if (e == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
			this.id != null && await e(this.id), this.__messages.set([]), this.__queuedSends.set([]), this.__restoredMessages.set([]), this.__respondedToolCallIds.clear(), this.__redispatchingToolCallIds.clear(), this.teardownToolsSubscription(), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set()), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
		} catch (e) {
			throw this.setStatus("error"), e;
		}
	}
	markOrphanedToolCalls() {
		let e = this.__messages.get(), t = this.__tools.get(), n = /* @__PURE__ */ new Set();
		for (let t of e) if (t.role === "user" && t.type === "tool_response") for (let e of t.tool_responses) n.add(e.tool_call_id);
		let r = new Set(this.__orphanedToolCallIds.get()), i = !1;
		for (let a of e) if (!(a.role !== "model" || a.type !== "tool_call")) for (let e of a.tool_calls) n.has(e.id) || e.needs_approval !== !0 && t[e.name]?.humanInTheLoop !== !0 && (r.has(e.id) || (r.add(e.id), i = !0));
		i && this.__orphanedToolCallIds.set(r);
	}
	executePendingClientToolCalls({ answerUnrunnable: e = !1, afterExchange: t } = {}) {
		if (this.status !== "ready") return Promise.resolve();
		let n = this.__messages.get(), r = t == null ? n : J(n, t), i = this.__tools.get(), a = this.__orphanedToolCallIds.get(), o = /* @__PURE__ */ new Set();
		for (let e of n) if (e.role === "user" && e.type === "tool_response") for (let t of e.tool_responses) o.add(t.tool_call_id);
		let s = [], c = !1;
		for (let t of r) if (!(t.role !== "model" || t.type !== "tool_call")) for (let n of t.tool_calls) {
			if (o.has(n.id) || a.has(n.id) || this.__redispatchingToolCallIds.has(n.id) || n.is_client_tool !== !0 || n.needs_approval === !0) continue;
			let t = i[n.name];
			if ((t == null || t.type === "server" || t.humanInTheLoop !== !0 && t.handler == null) && e) {
				s.push({
					id: n.id,
					name: n.name,
					args: n.args ?? {}
				});
				continue;
			}
			if (t == null) {
				c = !0;
				continue;
			}
			if (t.type !== "server" && t.humanInTheLoop !== !0) {
				if (t.handler == null) {
					c = !0;
					continue;
				}
				H(t.needsApproval, n.args) || s.push({
					id: n.id,
					name: n.name,
					args: n.args ?? {}
				});
			}
		}
		if (c ? this.ensureToolsSubscription() : this.teardownToolsSubscription(), s.length === 0) return Promise.resolve();
		for (let e of s) this.__redispatchingToolCallIds.add(e.id);
		return (async () => {
			try {
				let e = await Promise.all(s.map(async (e) => {
					let t = this.__tools.get()[e.name];
					try {
						if (t == null || t.type === "server" || t.humanInTheLoop === !0 || t.handler == null) throw Error(`Tool ${e.name} does not have a handler defined.`);
						let n = await t.handler({
							args: e.args,
							session: this,
							toolCallId: e.id
						}) ?? null;
						return {
							tool_call_id: e.id,
							name: e.name,
							response: {
								result: n,
								error: null
							},
							finished: !0
						};
					} catch (t) {
						return {
							tool_call_id: e.id,
							name: e.name,
							response: {
								result: null,
								error: `Error calling tool ${e.name}: ${t instanceof Error ? t.message : String(t)}`
							},
							finished: !0
						};
					}
				}));
				await this.sendMessage([{
					role: "user",
					type: "tool_response",
					tool_responses: e,
					timestamp: l()
				}]);
			} catch {} finally {
				for (let e of s) this.__redispatchingToolCallIds.delete(e.id);
			}
		})();
	}
	ensureToolsSubscription() {
		this.__toolsUnsub ??= this.__tools.listen(() => {
			this.executePendingClientToolCalls();
		});
	}
	teardownToolsSubscription() {
		this.__toolsUnsub != null && (this.__toolsUnsub(), this.__toolsUnsub = null);
	}
	dispose() {
		this.teardownToolsSubscription();
	}
	annotateClientToolApproval(e) {
		if (e.role !== "model" || e.type !== "tool_call") return e;
		let t = e.tool_calls.map((e) => {
			if (e.is_client_tool === !0 && !e.needs_approval) {
				let t = this.__tools.get()[e.name];
				if (H(t?.needsApproval, e.args)) return {
					...e,
					needs_approval: !0
				};
			}
			return e;
		});
		return {
			...e,
			tool_calls: t
		};
	}
	runServerToolHandler(e, t) {
		let n = this.__tools.get()[e.name];
		n?.type !== "server" || n.handler == null || e.finished !== !0 || n.handler({
			args: W(t, e.tool_call_id)?.args || {},
			session: this,
			response: e.response,
			toolCallId: e.tool_call_id,
			wasApproved: e.was_approved === !0
		});
	}
	getPendingApprovals() {
		let e = this.__messages.get(), t = /* @__PURE__ */ new Set();
		for (let n of e) if (n.role === "user" && n.type === "tool_response") for (let e of n.tool_responses) t.add(e.tool_call_id);
		let n = [];
		for (let r of e) if (r.role === "model" && r.type === "tool_call") for (let e of r.tool_calls) e.needs_approval === !0 && !t.has(e.id) && n.push({
			id: e.id,
			name: e.name
		});
		return n;
	}
	async sendMessage(e, t = {}) {
		let n = t.instructions ?? [], r = typeof e == "string" ? [{
			role: "user",
			type: "text",
			text: e,
			timestamp: l()
		}] : [...e];
		r = fe(r);
		let i = this.runStreamTransport();
		if (i == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
		if ((this.status === "idle" || this.status === "error") && await this.initialize(), this.status === "initializing" && await this.waitUntilReady(), this.status !== "ready") {
			if (this.status === "sending" || this.status === "receiving" || this.status === "recovering") {
				this.status === "recovering" && G(r) ? this.queueLatestUnsentPrompt(r, n) : this.queueSend(r, n);
				return;
			}
			throw Error(`Cannot send message while session is in "${this.status}" state.`);
		}
		if (!r.some((e) => e.type === "tool_response")) {
			let e = this.getPendingApprovals();
			e.length > 0 && (r = [ve(e), ...r]);
		}
		await this.sendWithRunStream(i, r, n);
	}
	runStreamTransport() {
		let { streamExchange: e, attachExchange: t } = window.__hydra_copilot__ ?? {};
		return e == null || t == null ? null : {
			streamExchange: e,
			attachExchange: t
		};
	}
	async sendWithRunStream(e, t, n) {
		let r = this.__messages.get(), i = t.map((e) => ({
			...e,
			timestamp: e.timestamp || l()
		})), a = new AbortController(), o = /* @__PURE__ */ new Set(), s;
		this.setStatus("sending"), this.__lastTurnStopped.set(!1), this.__abortController = a, this.__inFlightClientMessageId = de(i), this.__messages.set([...r, ...i]);
		let c = (e, t) => {
			this.setStatus("receiving");
			try {
				for (let n of t) if (!(n.role !== "user" || n.type !== "tool_response")) for (let t of n.tool_responses) t.finished !== !0 || o.has(t.tool_call_id) || (o.add(t.tool_call_id), this.runServerToolHandler(t, e));
			} catch (e) {
				throw s = e, e;
			}
		}, u;
		try {
			u = await this.followRun(e, {
				history: r,
				pending: i,
				controller: a,
				owns: () => this.status === "sending" || this.status === "receiving",
				onShown: c,
				open: async (t) => (await e.streamExchange({
					id: this.id,
					messages: i,
					tools: R(this.__tools.get()),
					instructions: [...z(this.__instructions.get()), ...n],
					context: V(this.__context.get()),
					onEvent: t,
					signal: a.signal,
					enable_web_search: this.getWebSearchEnabled(),
					approval_mode: this.getApprovalMode()
				}), !0)
			});
		} catch (t) {
			if (ge(t)) {
				this.settleReady();
				return;
			}
			if (s !== void 0) throw this.settleReadyAfterFailure(), s;
			if (_e(t)) {
				K(i).length > 0 && !this.hasQueuedUnsentPrompt() && this.queueLatestUnsentPrompt(i, n), this.attachToRun(e, r);
				return;
			}
			this.__messages.set([...r, ...i]), this.setStatus("recovering"), this.recoverInterruptedStream().catch(() => {
				this.status === "recovering" && this.settleReadyAfterFailure();
			});
			return;
		} finally {
			this.__abortController = null, this.__inFlightClientMessageId = void 0;
		}
		this.status !== "sending" && this.status !== "receiving" || (this.setStatus("recovering"), await this.adoptRun(r, {
			ownsClientTools: !0,
			failed: u === "failed"
		}).catch(() => {
			this.status === "recovering" && this.settleReadyAfterFailure();
		}));
	}
	startFollowingElsewhere() {
		this.__lastTurnStopped.set(!1), this.setStatus("recovering");
		let e = this.__recoveryGeneration;
		return () => this.status === "recovering" && this.__recoveryGeneration === e;
	}
	followExchangeRunningElsewhere(e) {
		let t = this.runStreamTransport();
		if (t == null) {
			this.watchExchangeRunningElsewhere(e);
			return;
		}
		this.attachToRun(t, pe(e));
	}
	attachToRun(e, t) {
		let n = this.startFollowingElsewhere(), r = new AbortController();
		(async () => {
			let i = await this.followRun(e, {
				history: t,
				pending: [],
				controller: r,
				owns: n,
				open: (t) => e.attachExchange({
					id: this.id,
					onEvent: t,
					signal: r.signal
				})
			});
			n() && await this.adoptRun(t, {
				ownsClientTools: !1,
				failed: i === "failed"
			});
		})().catch(() => {
			r.abort(), n() && this.watchExchangeRunningElsewhere(t);
		});
	}
	async followRun(e, { history: t, pending: n, controller: r, owns: i, open: o, onShown: s }) {
		let c = new Set(t.flatMap((e) => e.type === "meta" && e.meta?.id != null ? [e.meta.id] : [])), l = o, u = [0, 0], d = 0;
		for (; i();) {
			let o = new a(c), f = Date.now();
			if (!await l((e) => {
				if (!i()) {
					r.abort();
					return;
				}
				o.apply(e);
				let a = o.messages().map((e) => this.annotateClientToolApproval(e)), c = [
					...t,
					...o.hasCommits ? [] : n,
					...a
				];
				this.__messages.set(c), s?.(c, a);
			}) || o.ended != null) return o.ended;
			let p = o.progress;
			if (d = p[0] > u[0] || p[0] === u[0] && p[1] > u[1] || Date.now() - f >= g ? 0 : d + 1, u = p, d > h) throw Error("The run stream keeps closing without progress.");
			await new Promise((e) => setTimeout(e, 500 * d)), l = (t) => e.attachExchange({
				id: this.id,
				onEvent: t,
				signal: r.signal
			});
		}
	}
	async adoptRun(e, { ownsClientTools: t, failed: n }) {
		let r = this.__recoveryGeneration, i = await this.loadSession();
		if (this.status !== "recovering" || this.__recoveryGeneration !== r) return;
		let a = i.messages ?? [];
		if (!t && O(q(a))) {
			this.watchExchangeRunningElsewhere(e);
			return;
		}
		let o = D(e);
		n && E(a, o) <= E(e, o) && (a = [...a, {
			role: "model",
			type: "error",
			error: y,
			client_generated: !0,
			timestamp: l()
		}]), await this.adoptRecoveredSession({
			messages: a,
			meta: i.meta ?? {}
		}, o, {
			runPendingClientTools: t,
			answerUnrunnableClientTools: t
		});
	}
	async recoverInterruptedStream() {
		if (this.id == null || window.__hydra_copilot__?.getSession == null) {
			this.settleReadyAfterFailure();
			return;
		}
		let e = D(this.__messages.get()), t = (t) => E(t, e), n = t(this.__messages.get()), r = 0;
		for (; r < v;) {
			let i = this.__lastTurnStopped.get() ? p : x(r);
			if (await this.waitForRecoveryPoll(i) && (r += i), this.status !== "recovering") return;
			let a;
			try {
				let e = await this.loadSession();
				a = {
					messages: e.messages ?? [],
					meta: e.meta ?? {}
				};
			} catch {
				continue;
			}
			let o = D(a.messages), s = a.messages[a.messages.length - 1]?.type, c = o == null ? s === "meta" : o !== e, l = s === "error" && t(a.messages) > n;
			if (c || l) {
				await this.adoptRecoveredSession(a, e);
				return;
			}
		}
		this.status === "recovering" && (this.__messages.set([...this.__messages.get(), {
			role: "model",
			type: "error",
			error: "The connection was interrupted. Please try sending your message again.",
			client_generated: !0,
			timestamp: l()
		}]), this.settleReadyAfterFailure());
	}
	async adoptRecoveredSession(e, t, { runPendingClientTools: n = !0, sendQueuedMessages: r = !0, answerUnrunnableClientTools: i = !1 } = {}) {
		if (this.status !== "recovering") return;
		this.__messages.set(e.messages), this.__meta.set(e.meta), this.applyServerModel(e.meta), this.dropQueuedPromptsAlreadyInMessages(J(e.messages, t));
		let a = this.__lastTurnStopped.get();
		a && (this.markOrphanedToolCalls(), this.teardownToolsSubscription()), this.setStatus("ready"), !a && n && await this.executePendingClientToolCalls({
			answerUnrunnable: i,
			afterExchange: i ? t : void 0
		}), r ? this.drainAndSendQueuedMessages() : this.surfaceQueuedMessagesForRestore();
	}
	watchExchangeRunningElsewhere(e) {
		let t = this.startFollowingElsewhere(), n = D(e);
		(async () => {
			let e, r = !1, i = !1, a, o = 0;
			for (; o < v;) {
				let n = this.__lastTurnStopped.get() ? p : x(o);
				if (await this.waitForRecoveryPoll(n) && (o += n), !t()) return;
				let s;
				try {
					s = await this.loadSession();
				} catch {
					continue;
				}
				if (!t()) return;
				if (e = {
					messages: s.messages ?? [],
					meta: s.meta ?? {}
				}, s.exchangeRunning === !0) {
					a = void 0;
					continue;
				}
				if (!O(q(e.messages))) {
					r = !0;
					break;
				}
				if (a ??= o, o - a >= 6e4) {
					i = !0;
					break;
				}
			}
			if (t()) {
				if (e == null) {
					this.settleReadyAfterFailure();
					return;
				}
				await this.adoptRecoveredSession(e, n, {
					runPendingClientTools: i,
					sendQueuedMessages: r || i
				});
			}
		})().catch(() => {
			t() && this.settleReadyAfterFailure();
		});
	}
	drainAndSendQueuedMessages() {
		let e = Z(this.__queuedSends);
		e.messages.length !== 0 && this.sendMessage(e.messages, { instructions: e.instructions }).catch(() => {});
	}
	async loadSession() {
		let e = window.__hydra_copilot__?.getSession;
		if (e == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
		if (this.id == null) throw Error("Session not started");
		return await e(this.id);
	}
	async waitUntilReady() {
		if (this.status !== "ready") {
			if (this.status === "idle") {
				await this.initialize();
				return;
			}
			if (this.status === "error") throw Error("Session is in error state");
			if (this.status === "ending" || this.status === "ended") throw Error(`Cannot wait for session in "${this.status}" state`);
			return new Promise((e, t) => {
				let n = null, r = null, i = () => {
					n !== null && (clearTimeout(n), n = null), r !== null && (r(), r = null);
				};
				r = this.__status.subscribe((n) => {
					n === "ready" ? (i(), e()) : n === "error" ? (i(), t(/* @__PURE__ */ Error("Session failed while waiting for ready"))) : (n === "ending" || n === "ended") && (i(), t(/* @__PURE__ */ Error("Session ended while waiting for ready")));
				}), n = setTimeout(() => {
					i(), t(/* @__PURE__ */ Error("Waiting for session to be ready timed out - exceeded 30 seconds"));
				}, 3e4);
			});
		}
	}
	async getUploadSignature(e) {
		if (!this.id) throw Error("Session ID not available - ensure session is ready before calling getUploadSignature");
		let t = window.__hydra_copilot__;
		if (!t?.getUploadSignature) throw Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
		return await t.getUploadSignature({
			id: this.id,
			size: e.size,
			mime_type: e.mime_type
		});
	}
	async getFileDownloadUrl(e) {
		if (!this.id) throw Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
		let t = window.__hydra_copilot__;
		if (!t?.getFileDownloadUrl) throw Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
		return (await t.getFileDownloadUrl({
			id: this.id,
			filename: e
		})).url;
	}
	addUploadFiles(e) {
		let t = this.__uploadFiles.get();
		this.__uploadFiles.set([...t, ...e]);
	}
	updateUploadFile(e, t) {
		let n = this.__uploadFiles.get().map((n) => n.id === e ? {
			...n,
			...t
		} : n);
		this.__uploadFiles.set(n);
	}
	removeUploadFile(e) {
		let t = this.__uploadFiles.get().filter((t) => t.id !== e);
		this.__uploadFiles.set(t);
	}
	clearUploadFiles() {
		this.__uploadFiles.set([]);
	}
	getPendingUploadFiles() {
		return this.__uploadFiles.get().filter((e) => e.status === "pending");
	}
};
function A(e) {
	return typeof e == "string" ? e : typeof e == "number" || typeof e == "boolean" ? String(e) : Array.isArray(e) || typeof e == "object" ? JSON.stringify(e) : String(e);
}
function re(e) {
	let t = /* @__PURE__ */ new Map(), n = /* @__PURE__ */ new Set(), r = /* @__PURE__ */ new Set();
	return e.forEach((i, a) => {
		if (i.was_approved !== !0 || i.response == null || i.approval_request != null) return;
		let o = e.findIndex((e, t) => t !== a && !n.has(t) && e.name === i.name && e.needs_approval === !0 && e.approval_request == null && JSON.stringify(e.args) === JSON.stringify(i.args));
		o !== -1 && (t.set(o, {
			response: i.response,
			status: i.status
		}), n.add(o), r.add(a));
	}), {
		approvalResponses: t,
		claimedExecutions: r
	};
}
function j(e) {
	let t = e?.result;
	if (t == null) return [];
	let n = Array.isArray(t) ? t : [t], r = [];
	for (let e of n) {
		if (typeof e != "object" || !e) continue;
		let t = e;
		"stdout" in t || "exit_code" in t || typeof t.status == "string" && r.push(t.status);
	}
	return r;
}
var ie = new Set([
	"rejected",
	"denied",
	"unanswered"
]), ae = ["{\"error\":true", "{\"errors\":[{"];
function M(e) {
	if (e.status === "executing") return null;
	let t = e.response;
	if (t == null) return null;
	let n = j(t);
	if (n.some((e) => ie.has(e))) return null;
	let r = oe(e.name, t.result);
	return r == null && n.includes("partial") ? null : typeof t.error == "string" && t.error.length > 0 ? "tool_error" : r;
}
function oe(e, t) {
	let n = ce(t);
	for (let t of n) {
		let n = se(e, t);
		if (n != null) return n;
	}
	return n.length === 0 && typeof t == "string" && N(t) ? "error_envelope" : null;
}
function se(e, t) {
	if (t.error === !0 || Array.isArray(t.errors) && t.errors.length > 0) return "error_envelope";
	if (e === "bash") {
		let e = P(t.exit_code);
		if (e != null && e !== 0) return "exit_nonzero";
	}
	for (let e of [t.stderr, t.stdout]) if (typeof e == "string" && N(e)) return "error_envelope";
	return null;
}
function ce(e) {
	let t = Array.isArray(e) ? e : [e], n = [];
	for (let e of t) {
		let t = le(e);
		t != null && n.push(t);
	}
	return n;
}
function le(e) {
	let t = e;
	if (typeof e == "string") try {
		t = JSON.parse(e);
	} catch {
		return null;
	}
	if (typeof t != "object" || !t || Array.isArray(t)) return null;
	let n = new Map(Object.entries(t));
	return {
		error: n.get("error"),
		errors: n.get("errors"),
		exit_code: n.get("exit_code"),
		stderr: n.get("stderr"),
		stdout: n.get("stdout")
	};
}
function ue(e) {
	return e.status === "executing" ? "ok" : e.status === "error" ? "terminated" : M(e) == null ? "ok" : "unsuccessful";
}
function N(e) {
	let t = e.replace(/\s+/g, "");
	return ae.some((e) => t.startsWith(e));
}
function P(e) {
	if (typeof e == "number") return Number.isFinite(e) ? e : null;
	if (typeof e != "string" || e.trim() === "") return null;
	let t = Number(e);
	return Number.isFinite(t) ? t : null;
}
function F(e, t, n, r) {
	let i = [], a = [], o = [], s = [], c = {}, l = [];
	for (let t of e) {
		if (t.type !== "tool_response") {
			l.push(t);
			continue;
		}
		for (let e of t.tool_responses) e.tool_call_id != null && (c[e.tool_call_id] ?? (c[e.tool_call_id] = []), c[e.tool_call_id].push(e));
	}
	function u() {
		let e = a.filter((e) => e.type === "text"), t = a.find((e) => e.type === "meta" && "meta" in e);
		if (a = [], e.length === 0) return;
		let n = e.map((e) => e.text).join(""), r = t?.meta ?? {}, o = e[0]?.thought, s;
		if (o === !0) {
			let t = i[i.length - 1];
			s = t != null && t.timestamp != null ? Math.max(0, e[e.length - 1].timestamp - t.timestamp) : 0;
		}
		i.push({
			role: "model",
			type: "text",
			text: n,
			timestamp: e[0].timestamp,
			meta: r,
			...o === !0 && { thought: o },
			...s != null && { duration: s }
		}), a = [];
	}
	function d() {
		if (o.length === 0) return;
		let e = o.find((e) => e.type === "text"), t = o.filter((e) => e.type === "content"), n = o[0];
		i.push({
			role: "user",
			type: "text",
			text: e?.text ?? "",
			attachments: t.map((e) => ({
				content: e.content,
				mime_type: e.mime_type,
				display_name: e.display_name
			})),
			timestamp: n.timestamp ?? 0
		}), o = [];
	}
	function p(e) {
		let i = c[e.id], a = t[e.name], o = e.needs_approval === !0 || e.is_client_tool === !0 && H(a?.needsApproval, e.args), s = o ? {
			...e,
			needs_approval: !0
		} : e;
		if (i == null || i.length === 0) {
			let t = e.is_client_tool === !0 && a?.humanInTheLoop === !0;
			return n.has(e.id) && !o && !t ? {
				...s,
				status: "error",
				response: {
					result: null,
					error: f
				}
			} : {
				...s,
				status: "executing",
				...t && { respond: (t) => r(e.id, e.name, t) }
			};
		}
		let l = i.find((e) => e.response.error != null && e.response.error !== ""), u = i.some((e) => e.finished), d = i.some((e) => e.was_approved === !0);
		if (l != null) return {
			...s,
			status: "success",
			response: l.response,
			...d && { was_approved: !0 }
		};
		let p = a?.type === "server" && a?.isStreaming === !0;
		if (u && i.length === 1 && !p) return {
			...s,
			status: "success",
			response: i[0].response,
			...d && { was_approved: !0 }
		};
		let m = i.map((e) => e.response.result);
		return {
			...s,
			status: u ? "success" : "executing",
			response: {
				result: m,
				error: null
			},
			...d && { was_approved: !0 }
		};
	}
	function m() {
		if (s.length === 0) return;
		let e = s.flatMap((e) => e.tool_calls.map(p)), t = /* @__PURE__ */ new Map();
		for (let n of e) t.set(n.id, n);
		let n = Array.from(t.values()), { approvalResponses: r, claimedExecutions: a } = re(n), o = n.map((e, t) => {
			let n = r.get(t);
			return n == null ? e : {
				...e,
				response: n.response,
				status: n.status
			};
		}).filter((e, t) => !(a.has(t) || e.was_approved === !0 && e.approval_request == null));
		o.length > 0 && i.push({
			role: "model",
			type: "tool_call",
			tool_calls: o,
			timestamp: s[0].timestamp
		}), s = [];
	}
	for (let e of l) if (e.role === "model" && e.type === "text") {
		d(), m();
		let t = a.some((e) => e.type === "text"), n = e.thought === !0, r = a.some((e) => e.type === "text" && "thought" in e && e.thought === !0);
		t && (n && !r || !n && r) && u(), a.push(e);
	} else if (e.role === "user" && (e.type === "text" || e.type === "content")) u(), m(), e.type === "text" && o.some((e) => e.type === "text") && d(), o.push(e);
	else if (e.role === "model" && e.type === "meta") d(), a.push(e), u();
	else if (e.role === "model" && e.type === "tool_call") u(), d(), s.push(e);
	else if (e.role === "model" && e.type === "compaction_start") u(), d(), m(), i.push({
		role: "model",
		type: "compaction",
		status: "compacting",
		timestamp: e.timestamp
	});
	else if (e.role === "model" && e.type === "compaction_complete") {
		u(), d(), m();
		let t = i.findIndex((e) => e.role === "model" && e.type === "compaction" && e.status === "compacting"), n = {
			role: "model",
			type: "compaction",
			status: "complete",
			summary: e.text,
			timestamp: e.timestamp
		};
		t >= 0 ? i[t] = n : i.push(n);
	} else e.type === "error" || e.type === "subagent_event" ? (u(), d(), m(), i.push(e)) : console.error("Unknown message type", e);
	u(), d(), m();
	for (let e = 0; e < i.length; e++) {
		let t = i[e];
		t.role !== "model" || t.type !== "tool_call" || t.tool_calls.some((e) => e.status === "executing") && i.slice(e + 1).some((e) => e.role === "model" && (e.type === "text" || e.type === "tool_call" || e.type === "error")) && (i[e] = {
			...t,
			tool_calls: t.tool_calls.map((e) => e.status === "executing" ? {
				...e,
				status: "success"
			} : e)
		});
	}
	return i;
}
function I(e, t) {
	return t.map((t, n) => {
		let r = e[n];
		return r != null && L(r, t) ? r : t;
	});
}
function L(e, t) {
	if (e === t || typeof e == "function" && typeof t == "function") return !0;
	if (typeof e != "object" || typeof t != "object" || e == null || t == null) return !1;
	if (Array.isArray(e) || Array.isArray(t)) return Array.isArray(e) && Array.isArray(t) && e.length === t.length && e.every((e, n) => L(e, t[n]));
	let n = Object.entries(e), r = new Map(Object.entries(t));
	return n.length === r.size && n.every(([e, t]) => r.has(e) && L(t, r.get(e)));
}
function R(e) {
	return Object.values(e).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
		name: e.name,
		description: typeof e.description == "string" ? U(e.description) : e.description,
		request_json_schema: e.requestSchema,
		json_response_schema: e.responseSchema
	}));
}
function z(e) {
	return Array.isArray(e) ? e.map((e) => U(e.instruction).trim()) : Object.values(e).filter((e) => e !== void 0).map((e) => U(e.instruction).trim());
}
function B(e) {
	return e.type === "text" && "text" in e && i(e.text);
}
function V(e) {
	return Array.isArray(e) ? e.map((e) => ({
		description: U(e.description),
		value: A(typeof e.value == "function" ? e.value() : e.value)
	})) : Object.values(e).filter((e) => e !== void 0).map((e) => ({
		description: U(e.description),
		value: A(typeof e.value == "function" ? e.value() : e.value)
	}));
}
function H(e, t) {
	if (typeof e == "function") try {
		return e(t);
	} catch {
		return !0;
	}
	return e === !0;
}
function U(e) {
	let t = e.split("\n"), n = t.filter((e) => e.trim().length > 0);
	if (n.length === 0) return e;
	let r = n.map((e) => {
		let t = e.match(/^(\s*)/);
		return t ? t[1].length : 0;
	}), i = Math.min(...r);
	return t.map((e) => e.trim().length === 0 ? e : e.slice(i)).join("\n");
}
function W(e, t) {
	for (let n of e) if (n.role === "model" && n.type === "tool_call") {
		let e = n.tool_calls.find((e) => e.id === t);
		if (e) return e;
	}
}
function G(e) {
	return e.length > 0 && e.every((e) => e.role === "user" && e.type !== "tool_response");
}
function K(e) {
	return e.filter((e) => e.role === "user" && e.type !== "tool_response");
}
function de(e) {
	for (let t = e.length - 1; t >= 0; t--) {
		let n = e[t];
		if (n.role === "user" && n.client_message_id != null && n.client_message_id.length > 0) return n.client_message_id;
	}
}
function fe(e) {
	return e.map((e) => {
		switch (e.role) {
			case "model": return e;
			case "user": return e.client_message_id != null && e.client_message_id.length > 0 ? e : {
				...e,
				client_message_id: r()
			};
			default: return e;
		}
	});
}
function pe(e) {
	for (let t = e.length - 1; t >= 0; t--) if (e[t].type === "meta" || e[t].type === "error") return e.slice(0, t + 1);
	return [];
}
function q(e) {
	let t = !1;
	for (let n = e.length - 1; n >= 0; n--) if (e[n].type === "meta") {
		if (t) return e.slice(n + 1);
		t = !0;
	}
	return e;
}
function J(e, t) {
	if (t == null) return e;
	for (let n = e.length - 1; n >= 0; n--) {
		let r = e[n];
		if (r.type === "meta" && r.meta?.id === t) return e.slice(n + 1);
	}
	return e;
}
function me(e, t) {
	if (t.length === 0 || t.length > e.length) return !1;
	for (let n = 0; n <= e.length - t.length; n++) if (t.every((t, r) => he(e[n + r], t))) return !0;
	return !1;
}
function he(e, t) {
	let n = Y(e), r = Y(t);
	return n != null && r != null ? n === r : e.role !== "user" || t.role !== "user" || e.type !== t.type ? !1 : e.type === "text" && t.type === "text" ? e.text === t.text : e.type === "content" && t.type === "content" ? e.content === t.content && e.mime_type === t.mime_type : JSON.stringify(X(e)) === JSON.stringify(X(t));
}
function Y(e) {
	if (e.role === "user") return e.client_message_id != null && e.client_message_id.length > 0 ? e.client_message_id : void 0;
}
function X(e) {
	let { timestamp: t, ...n } = e;
	return n;
}
function Z(e) {
	let t = e.get(), n = t[0], r = n?.messages ?? [], i = n?.instructions ?? [];
	if (r.length === 0) return e.set([]), {
		messages: [],
		instructions: []
	};
	e.set(t.slice(1));
	let a = r.filter((e) => e.type === "text" && "text" in e), o = [...r.filter((e) => e.type !== "text")];
	if (a.length > 0) if (a.length === 1) o.push(a[0]);
	else {
		let e = a.map((e) => "text" in e ? e.text : "").join("\n\n");
		o.push({
			role: "user",
			type: "text",
			text: e,
			timestamp: l()
		});
	}
	return {
		messages: o,
		instructions: i
	};
}
function ge(e) {
	return !!(e instanceof DOMException && e.name === "AbortError" || typeof e == "object" && e && "name" in e && e.name === "CanceledError");
}
function _e(e) {
	return e instanceof u ? !0 : Q(e) === 409;
}
function Q(e) {
	if (typeof e != "object" || !e) return;
	let t = "response" in e ? e.response : void 0;
	return typeof t == "object" && t && "status" in t && typeof t.status == "number" ? t.status : Q("cause" in e ? e.cause : void 0);
}
function ve(e) {
	return {
		role: "user",
		type: "tool_response",
		tool_responses: e.map((e) => ({
			tool_call_id: e.id,
			name: e.name,
			response: {
				result: { status: "rejected" },
				error: null
			},
			finished: !0
		})),
		timestamp: l()
	};
}
function $(e) {
	let t = {};
	for (let n of e) t[n.name] = {
		type: "server",
		name: n.name,
		description: n.description,
		requestSchema: n.request_json_schema,
		responseSchema: n.response_json_schema
	};
	return t;
}
//#endregion
export { u as CopilotActiveExchangeError, k as CopilotSession, f as ORPHANED_TOOL_CALL_ERROR, b as OTHER_WINDOW_TOOL_WAIT_MS, v as RECOVERY_POLL_CEILING_MS, _ as RECOVERY_STOP_RECONCILIATION_MS, m as RUN_REATTACH_DELAY_MS, j as capabilityDecisionStatuses, M as classifyToolFailure, W as findToolCallById, x as nextRecoveryDelayMs, V as prepareContextForTransport, z as prepareInstructionsForTransport, R as prepareToolsForTransport, H as resolveNeedsApproval, $ as serverSideToolsToCopilotTools, A as stringifyForCopilot, ue as toolCallOutcome, U as undent, w as updatedStatus };
